import axios from "axios";
import { useRef, useState } from "react";
import { ApplicantInfo } from "../../../components/Applicant/ApplicantInfo";
import { RadioButtonGroup } from "../../../components/Decision/RadioButtonGroup";
import ErrorMessages from "../../../components/ErrorMessages/ErrorMessage";
import { FormNumber } from "../../../components/Input/FormNumber";
import { FormTextArea } from "../../../components/Input/FormTextArea";
import { LoadingSpinner } from "../../../components/Spinner/LoadingSpinner";
import type { TaskProps } from "../../../types/TaskProps";
import { uploadFile } from "../../../utils/files";
import { CommonUserTaskNew } from "../CommonUserTaskNew";
import type { ComponentConfig } from "../types";

const MAX_APPROVED_INCOME = 150000;

interface UploadedDoc {
  fileId: string;
  filename: string;
  attachmentKey: string;
  [k: string]: unknown;
}

const toAttachmentKey = (filename: string): string =>
  filename.trim().replace(/[^A-Za-z0-9_-]+/g, "-");

const ManualTinkConsentReview = (props: TaskProps) => {
  const { flow, task } = props;

  const loadedDecision = task?.data?.decision ?? "";
  const loadedComment = task?.data?.comment ?? "";
  const loadedIncome = task?.data?.approvedIncome ?? "";

  const [decision, setDecision] = useState<string>(loadedDecision);
  const [comment, setComment] = useState<string>(loadedComment);
  const [approvedIncome, setApprovedIncome] = useState<string>(
    loadedIncome === "" ? "" : String(loadedIncome),
  );
  const [uploadedFiles, setUploadedFiles] = useState<UploadedDoc[]>(
    task?.data?.documents ?? [],
  );
  const [errorMessages, setErrorMessages] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const applicantInfo = flow?.data?.agentApiApplication?.applicant;
  const isApprove = decision === "approved";

  const handleFileUpload = async (files: File[]) => {
    setIsLoading(true);
    try {
      setErrorMessages("");
      const { uploadedFilesResponse } = await uploadFile(files);
      const newDocs: UploadedDoc[] = (uploadedFilesResponse ?? []).map(
        (response: { fileId: string; filename: string }) => ({
          ...response,
          attachmentKey: toAttachmentKey(response.filename),
        }),
      );
      setUploadedFiles((prev) => {
        const newIds = new Set(newDocs.map((d) => d.fileId));
        return [...prev.filter((d) => !newIds.has(d.fileId)), ...newDocs];
      });
      setIsLoading(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorMessages(
          `Could not upload document. Received error: ${JSON.stringify(error?.message)}`,
        );
      }
      setIsLoading(false);
    }
  };

  const handleFileRemove = (doc: UploadedDoc) => {
    setUploadedFiles((prev) => prev.filter((d) => d.fileId !== doc.fileId));
  };

  const buildFormData = (): Record<string, unknown> => {
    const formData: Record<string, unknown> = { decision, comment };
    if (isApprove) {
      if (approvedIncome !== "") {
        formData.approvedIncome = Number(approvedIncome);
      }
      if (uploadedFiles.length > 0) {
        formData.documents = uploadedFiles;
      }
    }
    return formData;
  };

  const onComplete = () => {
    if (decision === "") {
      setErrorMessages("Please select a decision");
      return;
    }

    if (isApprove) {
      const income = Number(approvedIncome);
      if (!approvedIncome || Number.isNaN(income) || income <= 0) {
        setErrorMessages("Please enter an approved income greater than 0.");
        return;
      }
      if (income > MAX_APPROVED_INCOME) {
        setErrorMessages(
          `Approved income cannot exceed ${MAX_APPROVED_INCOME}.`,
        );
        return;
      }
      if (comment.trim() === "") {
        setErrorMessages("A comment is required to approve the application.");
        return;
      }
      if (uploadedFiles.length < 1) {
        setErrorMessages(
          "Please upload at least one document to approve the application.",
        );
        return;
      }
    }

    props.complete(
      buildFormData(),
      () => {},
      (e: unknown) => {
        setErrorMessages((e as { detail?: string })?.detail ?? "");
      },
    );
  };

  const onSave = () => {
    props.save(
      buildFormData(),
      () => {},
      (error: unknown) => console.log("Save failed", error),
      true,
    );
  };

  const formConfig: ComponentConfig[] = [
    {
      component: <ApplicantInfo applicant={applicantInfo} />,
      order: 1,
    },
    {
      component: (
        <RadioButtonGroup
          radioButtonValue={decision}
          onChange={(e: string) => setDecision(e)}
          title="Decision"
          options={[
            { value: "retry", label: "Retry Tink Consent" },
            { value: "declined", label: "Decline application" },
            { value: "approved", label: "Approve application" },
          ]}
          id="manual-tink-consent-review"
        />
      ),
      order: 2,
    },
    {
      component: (
        <FormNumber
          name="approvedIncome"
          id="approved-income"
          label="Monthly net income"
          value={approvedIncome}
          onChange={(e: string) => setApprovedIncome(e)}
          min={1}
          max={MAX_APPROVED_INCOME}
          required
          placeholder="Enter the applicant's monthly net income"
        />
      ),
      order: 3,
      hide: !isApprove,
    },
    {
      component: (
        <div style={{ padding: "0 28px" }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Documentation</div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.json"
            style={{ display: "none" }}
            onChange={(e) => {
              const files = e.target.files ? Array.from(e.target.files) : [];
              if (files.length > 0) {
                handleFileUpload(files);
              }
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: 8,
              border: "2px solid #1b4df5",
              background: "transparent",
              color: "#1b4df5",
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
            Upload document
          </button>
          {uploadedFiles.length > 0 && (
            <ul style={{ listStyle: "none", padding: 0, marginTop: 8 }}>
              {uploadedFiles.map((doc) => (
                <li
                  key={doc.fileId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "4px 0",
                  }}
                >
                  <span>{doc.filename}</span>
                  <button
                    type="button"
                    onClick={() => handleFileRemove(doc)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#E4251B",
                      cursor: "pointer",
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ),
      order: 4,
      hide: !isApprove,
    },
    {
      component: <LoadingSpinner />,
      order: 5,
      hide: !isLoading,
    },
    {
      component: (
        <FormTextArea
          name="Comment"
          onChange={(e: string) => setComment(e)}
          label="Comment"
          defaultValue={comment || ""}
          placeholder={
            isApprove
              ? "A comment is required to approve the application"
              : "Enter your comment here (optional)"
          }
        />
      ),
      order: 6,
    },
    {
      component: (
        <div style={{ padding: "0 28px" }}>
          <ErrorMessages errors={[errorMessages]} />
        </div>
      ),
      order: 7,
      hide: errorMessages === "",
    },
  ];

  return (
    <CommonUserTaskNew
      handleComplete={onComplete}
      handleSave={onSave}
      formConfig={formConfig}
      formWidth={480}
    />
  );
};

export default ManualTinkConsentReview;
